import { useState, useEffect, useRef, useCallback } from 'react';
import { Camera, Check, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { visitRecordsApi, type PhotoSlot } from '@/api/visitRecords';

interface CheckoutDetailsModalProps {
  open: boolean;
  onClose: () => void;
  businessId: string;
  appointmentId: number;
  /** Fires the appointment's own state transition — called regardless of whether the
   *  visit-record save succeeded, since logging checkout details must never block completion. */
  onComplete: () => void;
  /** True while the parent's own completion mutation is in flight, to keep the buttons disabled. */
  completing: boolean;
}

const labelStyle = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 500,
  color: 'var(--muted-fg)',
  marginBottom: '6px',
} as const;

const fieldStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: '12px',
  border: '1px solid var(--border-color)',
  backgroundColor: 'var(--off-white)',
  fontSize: '14px',
  color: 'var(--dark-teal)',
} as const;

function PhotoSlotPicker({
  label,
  uploading,
  uploaded,
  disabled,
  onSelect,
}: {
  label: string;
  uploading: boolean;
  uploaded: boolean;
  disabled: boolean;
  onSelect: (file: File) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp"
        className="hidden"
        onChange={e => {
          const file = e.target.files?.[0];
          if (file) onSelect(file);
          e.target.value = '';
        }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading || disabled}
        className="w-full rounded-xl flex flex-col items-center justify-center gap-2 transition-colors"
        style={{
          height: '104px',
          border: uploaded ? '2px solid var(--terracotta)' : '2px dashed var(--border-color)',
          backgroundColor: 'var(--muted-bg)',
          cursor: uploading || disabled ? 'not-allowed' : 'pointer',
        }}
      >
        {uploading ? (
          <Loader2 size={20} className="animate-spin" style={{ color: 'var(--terracotta)' }} />
        ) : uploaded ? (
          <Check size={20} style={{ color: 'var(--terracotta)' }} />
        ) : (
          <Camera size={20} stroke="var(--dark-teal)" strokeWidth={1.5} />
        )}
        <span style={{ fontSize: '12px', color: 'var(--dark-teal)', fontWeight: 500 }}>
          {uploaded ? 'Reemplazar' : label}
        </span>
      </button>
    </div>
  );
}

export function CheckoutDetailsModal({
  open,
  onClose,
  businessId,
  appointmentId,
  onComplete,
  completing,
}: CheckoutDetailsModalProps) {
  const [treatmentGoal, setTreatmentGoal] = useState('');
  const [productsUsed, setProductsUsed] = useState('');
  const [notes, setNotes] = useState('');
  const [hasAllergyFlag, setHasAllergyFlag] = useState(false);
  const [allergyFlagDetail, setAllergyFlagDetail] = useState('');
  const [saving, setSaving] = useState(false);

  const [beforeUploading, setBeforeUploading] = useState(false);
  const [afterUploading, setAfterUploading] = useState(false);
  const [beforeUploaded, setBeforeUploaded] = useState(false);
  const [afterUploaded, setAfterUploaded] = useState(false);

  // Reset on every open so a previous checkout's in-progress state never leaks into the next.
  useEffect(() => {
    if (!open) return;
    setTreatmentGoal('');
    setProductsUsed('');
    setNotes('');
    setHasAllergyFlag(false);
    setAllergyFlagDetail('');
    setSaving(false);
    setBeforeUploading(false);
    setAfterUploading(false);
    setBeforeUploaded(false);
    setAfterUploaded(false);
  }, [open]);

  const handleClose = useCallback(() => {
    if (saving || completing) return;
    onClose();
  }, [saving, completing, onClose]);

  if (!open) return null;

  // Photos upload immediately on selection (like the team-avatar upload elsewhere in the app),
  // independent of the text fields — a failed upload only toasts, it never blocks the checkout.
  const handlePhotoSelect = async (slot: PhotoSlot, file: File) => {
    const setUploading = slot === 'before' ? setBeforeUploading : setAfterUploading;
    const setUploaded = slot === 'before' ? setBeforeUploaded : setAfterUploaded;
    setUploading(true);
    try {
      await visitRecordsApi.uploadPhoto(businessId, appointmentId, slot, file);
      setUploaded(true);
      toast.success(slot === 'before' ? 'Foto de antes guardada' : 'Foto de después guardada');
    } catch {
      toast.error('No se pudo subir la foto');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveAndComplete = async () => {
    setSaving(true);
    try {
      await visitRecordsApi.save(businessId, appointmentId, {
        treatmentGoal: treatmentGoal.trim() || undefined,
        productsUsed: productsUsed.trim() || undefined,
        notes: notes.trim() || undefined,
        hasAllergyFlag,
        allergyFlagDetail: hasAllergyFlag ? allergyFlagDetail.trim() || undefined : undefined,
      });
    } catch {
      toast.error('No se pudo guardar el detalle de la visita — la cita se completará de todas formas');
    } finally {
      setSaving(false);
      onComplete();
    }
  };

  const busy = saving || completing;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={handleClose}
      role="presentation"
    >
      <div
        className="rounded-2xl w-full max-w-lg flex flex-col"
        style={{
          backgroundColor: 'var(--off-white)',
          border: '1px solid var(--border-color)',
          boxShadow: 'var(--shadow-lg)',
          maxHeight: 'calc(100vh - 32px)',
        }}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="checkout-details-title"
      >
        <div className="flex items-start justify-between gap-3" style={{ padding: '20px 20px 0' }}>
          <div>
            <h2
              id="checkout-details-title"
              style={{ fontFamily: 'var(--font-heading)', fontSize: '20px', color: 'var(--dark-teal)' }}
            >
              Detalle de la visita
            </h2>
            <p style={{ fontSize: '13px', color: 'var(--muted-fg)', marginTop: '4px', lineHeight: 1.4 }}>
              Todo es opcional — puedes completar la cita sin llenar nada.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Cerrar"
            className="rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ width: '32px', height: '32px', color: 'var(--muted-fg)' }}
          >
            <X size={18} />
          </button>
        </div>

        <div className="overflow-auto" style={{ padding: '16px 20px' }}>
          <div className="mb-4">
            <label style={labelStyle} htmlFor="checkout-treatment-goal">Objetivo del tratamiento</label>
            <input
              id="checkout-treatment-goal"
              type="text"
              maxLength={255}
              value={treatmentGoal}
              onChange={e => setTreatmentGoal(e.target.value)}
              disabled={busy}
              placeholder="Ej. Hidratación profunda"
              style={fieldStyle}
            />
          </div>

          <div className="mb-4">
            <label style={labelStyle} htmlFor="checkout-products-used">Productos usados</label>
            <textarea
              id="checkout-products-used"
              rows={2}
              maxLength={1000}
              value={productsUsed}
              onChange={e => setProductsUsed(e.target.value)}
              disabled={busy}
              placeholder="Ej. Champú hidratante, mascarilla reparadora"
              style={{ ...fieldStyle, resize: 'vertical' }}
            />
          </div>

          <div className="mb-4">
            <label style={labelStyle} htmlFor="checkout-notes">Notas de la visita</label>
            <textarea
              id="checkout-notes"
              rows={3}
              maxLength={2000}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              disabled={busy}
              placeholder="Observaciones, resultado, recomendaciones para la próxima visita..."
              style={{ ...fieldStyle, resize: 'vertical' }}
            />
          </div>

          <div className="mb-4">
            <label className="flex items-center gap-2" style={{ fontSize: '13px', color: 'var(--dark-teal)' }}>
              <input
                type="checkbox"
                checked={hasAllergyFlag}
                onChange={e => setHasAllergyFlag(e.target.checked)}
                disabled={busy}
              />
              Marcar reacción o alergia detectada en esta visita
            </label>
            {hasAllergyFlag && (
              <textarea
                rows={2}
                maxLength={500}
                value={allergyFlagDetail}
                onChange={e => setAllergyFlagDetail(e.target.value)}
                disabled={busy}
                placeholder="Describe lo ocurrido"
                style={{ ...fieldStyle, resize: 'vertical', marginTop: '8px' }}
              />
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <PhotoSlotPicker
              label="Foto antes"
              uploading={beforeUploading}
              uploaded={beforeUploaded}
              disabled={busy}
              onSelect={file => handlePhotoSelect('before', file)}
            />
            <PhotoSlotPicker
              label="Foto después"
              uploading={afterUploading}
              uploaded={afterUploaded}
              disabled={busy}
              onSelect={file => handlePhotoSelect('after', file)}
            />
          </div>
        </div>

        <div
          className="flex gap-3 flex-shrink-0"
          style={{ padding: '16px 20px', borderTop: '1px solid var(--border-color)' }}
        >
          <button
            type="button"
            onClick={onComplete}
            disabled={busy}
            className="rounded-xl"
            style={{
              height: '44px',
              padding: '0 16px',
              border: '1px solid var(--border-color)',
              color: 'var(--dark-teal)',
              fontSize: '14px',
              whiteSpace: 'nowrap',
            }}
          >
            Completar sin detalles
          </button>
          <button
            type="button"
            onClick={handleSaveAndComplete}
            disabled={busy}
            className="flex-1 rounded-xl flex items-center justify-center gap-2"
            style={{
              height: '44px',
              backgroundColor: 'var(--terracotta)',
              color: 'white',
              fontSize: '14px',
              fontWeight: 500,
              border: 'none',
              cursor: busy ? 'not-allowed' : 'pointer',
              opacity: busy ? 0.7 : 1,
            }}
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Guardar y completar
          </button>
        </div>
      </div>
    </div>
  );
}
